import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { CurrencyField } from '@pesto/ui/components/Forms/CurrencyField';
import { z } from 'zod';

import type { CreateProgramInput } from '../../../__generated__/graphql/api';
import type { ModalProps } from '../../types/modal.types';

import {
  AccrualBase,
  GraceStrategy,
  InterestPostingPolicy,
  LateFeeStrategy,
  MinimumPaymentAllocationOrder,
  MinimumPaymentClassOrder,
  MinimumPaymentStrategy,
  PdfTemplate,
  ReturnedPaymentFeeStrategy,
  useCreateProgramMutation,
  useListPartnersQuery,
} from '../../../__generated__/graphql/api';
import { getProgramMutationRefreshOptions } from '../../../utils/programCacheInvalidation';
import { PDF_TEMPLATES_BY_POLICY, toPdfTemplateOptions } from '../../../utils/programPdfTemplates';
import { createFormComponent } from '../../utils/create-form-component';
import { YES_NO_OPTIONS } from '../../utils/form-helpers';
import {
  billingDaySchema,
  optionalUuidSchema,
  PERCENT_PATTERN,
  percentFieldSchema,
  percentStringToRate,
} from './programFormSchemas';

const creditCardStrategyCondition = {
  when: {
    field: 'minimumPaymentStrategy' as const,
    operator: 'equals' as const,
    value: MinimumPaymentStrategy.CreditCardPercentOfBalance,
  },
};

const PROGRAM_CONFIGURATION_BY_POLICY = {
  [InterestPostingPolicy.TrailingNextStatement]: {
    accrualBase: AccrualBase.PrincipalOnly,
    minimumPaymentStrategy: MinimumPaymentStrategy.HelocCumulative,
  },
  [InterestPostingPolicy.SameCycleStatementClose]: {
    accrualBase: AccrualBase.FullBalance,
    minimumPaymentStrategy: MinimumPaymentStrategy.CreditCardPercentOfBalance,
  },
} satisfies Record<
  InterestPostingPolicy,
  {
    accrualBase: AccrualBase;
    minimumPaymentStrategy: MinimumPaymentStrategy;
  }
>;

export const createProgramSchema = z
  .object({
    program_id: z.string().min(1, 'Program ID is required'),
    partnerId: z.string().min(1, 'Partner is required'),
    twispJournalId: optionalUuidSchema('Twisp Journal ID'),
    twispIsoConfigId: optionalUuidSchema('Twisp ISO Config ID'),
    twispExposureAccountSetId: optionalUuidSchema('Twisp Exposure Account Set ID'),
    name: z.string().min(1, 'Name is required'),
    targetInterestRate: z
      .string()
      .min(1, 'Interest rate is required')
      .regex(/^\d+(\.\d+)?$/, 'Interest rate must be a valid number')
      .refine((val) => parseFloat(val) >= 0 && parseFloat(val) <= 100, 'Interest rate must be between 0 and 100'),
    minApr: z
      .string()
      .min(1, 'Min APR is required')
      .regex(/^\d+(\.\d+)?$/, 'Min APR must be a valid number')
      .refine((val) => parseFloat(val) >= 0 && parseFloat(val) <= 100, 'Min APR must be between 0 and 100'),
    maxApr: z
      .string()
      .min(1, 'Max APR is required')
      .regex(/^\d+(\.\d+)?$/, 'Max APR must be a valid number')
      .refine((val) => parseFloat(val) >= 0 && parseFloat(val) <= 100, 'Max APR must be between 0 and 100'),
    firstLateFeeAmount: z
      .number()
      .min(1, 'First late fee amount is required')
      .refine((val) => val >= 0, 'First late fee amount must be positive'),
    subsequentLateFeeAmount: z
      .number()
      .refine((val) => val >= 0, 'Subsequent late fee amount must be positive')
      .optional(),
    lateFeeStrategy: z.nativeEnum(LateFeeStrategy, {
      errorMap: () => ({ message: 'Late fee strategy is required' }),
    }),
    returnedPaymentFeeCents: z
      .number({ required_error: 'Returned payment fee is required' })
      .refine((val) => val >= 0, 'Returned payment fee must be positive'),
    returnedPaymentFeeStrategy: z.nativeEnum(ReturnedPaymentFeeStrategy, {
      errorMap: () => ({ message: 'Returned payment fee strategy is required' }),
    }),
    foreignTransactionFeeRatePercent: percentFieldSchema('Foreign transaction fee rate'),
    cashAdvancesAllowed: z.boolean(),
    autopayMinimum: z.boolean(),
    autopayStatement: z.boolean(),
    autopayFull: z.boolean(),
    cashAdvanceFeeRatePercent: percentFieldSchema('Cash advance fee rate'),
    cashAdvanceFeeMinCents: z
      .number({ required_error: 'Cash advance fee minimum is required' })
      .refine((val) => val >= 0, 'Cash advance fee minimum must be positive'),
    replacementCardFeeCents: z
      .number({ required_error: 'Replacement card fee is required' })
      .refine((val) => val >= 0, 'Replacement card fee must be positive'),
    paymentDueDateGracePeriodDays: z
      .string()
      .min(1, 'Minimum close-to-due days is required')
      .regex(/^\d+$/, 'Minimum close-to-due days must be a whole number')
      .refine((val) => parseInt(val) >= 0 && parseInt(val) <= 28, 'Minimum close-to-due days must be between 0 and 28'),
    lateFeeGracePeriodDays: z
      .string()
      .min(1, 'Late fee grace period days is required')
      .regex(/^\d+$/, 'Late fee grace period days must be a whole number')
      .refine(
        (val) => parseInt(val) >= 0 && parseInt(val) <= 28,
        'Late fee grace period days must be between 0 and 28',
      ),
    statementGenerateDay: billingDaySchema('Statement generation day'),
    paymentDueDay: billingDaySchema('Payment due day'),
    minimumPaymentStrategy: z.nativeEnum(MinimumPaymentStrategy),
    minimumPaymentAllocationOrder: z.nativeEnum(MinimumPaymentAllocationOrder, {
      errorMap: () => ({ message: 'Minimum payment allocation order is required' }),
    }),
    minimumPaymentClassOrder: z.nativeEnum(MinimumPaymentClassOrder, {
      errorMap: () => ({ message: 'Minimum payment class order is required' }),
    }),
    minimumPaymentRatePercent: z.string().optional(),
    minimumPaymentFloorCents: z.number().optional(),
    graceStrategy: z.nativeEnum(GraceStrategy, {
      errorMap: () => ({ message: 'Grace strategy is required' }),
    }),
    accrualBase: z.nativeEnum(AccrualBase, { errorMap: () => ({ message: 'Accrual base is required' }) }),
    interestPostingPolicy: z.nativeEnum(InterestPostingPolicy, {
      errorMap: () => ({ message: 'Interest posting policy is required' }),
    }),
    pdfTemplate: z.nativeEnum(PdfTemplate, {
      errorMap: () => ({ message: 'Statement PDF template is required' }),
    }),
    chargeDrawdownFee: z.enum(['true', 'false'], {
      errorMap: () => ({ message: 'Drawdown fee setting is required' }),
    }),
  })
  .refine((data) => parseFloat(data.minApr) <= parseFloat(data.maxApr), {
    message: 'Max APR must be greater than or equal to Min APR',
    path: ['maxApr'],
  })
  .superRefine((data, ctx) => {
    if (data.statementGenerateDay && Number(data.statementGenerateDay) === Number(data.paymentDueDay)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['paymentDueDay'],
        message: 'Payment due day must differ from statement generation day.',
      });
    }

    if (data.lateFeeStrategy === LateFeeStrategy.FlatTiered && data.subsequentLateFeeAmount === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['subsequentLateFeeAmount'],
        message: 'Subsequent late fee amount is required for flat tiered fees',
      });
    }

    if (!data.autopayMinimum && !data.autopayStatement && !data.autopayFull) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['autopayMinimum'],
        message: 'Offer at least one autopay payment type',
      });
    }

    const expectedConfiguration = PROGRAM_CONFIGURATION_BY_POLICY[data.interestPostingPolicy];

    if (data.accrualBase !== expectedConfiguration.accrualBase) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['accrualBase'],
        message:
          data.interestPostingPolicy === InterestPostingPolicy.TrailingNextStatement
            ? 'Trailing interest programs must accrue on principal only'
            : 'Same-cycle interest programs must accrue on the full balance',
      });
    }

    if (!PDF_TEMPLATES_BY_POLICY[data.interestPostingPolicy].includes(data.pdfTemplate)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pdfTemplate'],
        message:
          data.interestPostingPolicy === InterestPostingPolicy.TrailingNextStatement
            ? 'Trailing interest programs must use the HomeLight statement template'
            : 'Same-cycle interest programs must use the LFCU or generic card statement template',
      });
    }

    if (data.minimumPaymentStrategy !== expectedConfiguration.minimumPaymentStrategy) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['minimumPaymentStrategy'],
        message:
          data.interestPostingPolicy === InterestPostingPolicy.TrailingNextStatement
            ? 'Trailing interest programs must use the HELOC minimum payment strategy'
            : 'Same-cycle interest programs must use the credit card minimum payment strategy',
      });
    }

    if (data.minimumPaymentStrategy !== MinimumPaymentStrategy.CreditCardPercentOfBalance) return;

    if (data.graceStrategy === GraceStrategy.Retroactive) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['graceStrategy'],
        message: 'Credit-card programs must use the waive model (Reg Z §1026.54)',
      });
    }

    if (data.returnedPaymentFeeStrategy === ReturnedPaymentFeeStrategy.Flat) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['returnedPaymentFeeStrategy'],
        message: 'Credit-card programs must use the capped model (Reg Z §1026.52(b)(2)(i))',
      });
    }

    const rawRate = data.minimumPaymentRatePercent ?? '';

    if (!rawRate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['minimumPaymentRatePercent'],
        message: 'Minimum payment rate is required',
      });
    } else if (!PERCENT_PATTERN.test(rawRate)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['minimumPaymentRatePercent'],
        message: 'Minimum payment rate must be a percent with at most two decimals',
      });
    } else {
      const parsed = parseFloat(rawRate);

      if (parsed < 0 || parsed > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['minimumPaymentRatePercent'],
          message: 'Minimum payment rate must be between 0 and 100',
        });
      }
    }

    if (data.minimumPaymentFloorCents === undefined || data.minimumPaymentFloorCents === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['minimumPaymentFloorCents'],
        message: 'Minimum payment floor is required',
      });
    } else if (data.minimumPaymentFloorCents < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['minimumPaymentFloorCents'],
        message: 'Minimum payment floor must be greater than or equal to 0',
      });
    }
  });

type CreateProgramFormData = z.infer<typeof createProgramSchema>;

const minimumPaymentStrategyOptions = [
  {
    value: MinimumPaymentStrategy.HelocCumulative,
    label: 'HELOC (cumulative interest + fees)',
  },
  {
    value: MinimumPaymentStrategy.CreditCardPercentOfBalance,
    label: 'Credit card (% of balance)',
  },
];

const minimumPaymentAllocationOrderOptions = [
  {
    value: MinimumPaymentAllocationOrder.LowestAprFirst,
    label: 'Lowest APR first — the required minimum pays the cheapest bucket first',
  },
  {
    value: MinimumPaymentAllocationOrder.HighestAprFirst,
    label: 'Highest APR first — the required minimum pays the dearest bucket first',
  },
];

const minimumPaymentClassOrderOptions = [
  {
    value: MinimumPaymentClassOrder.InterestBeforePrincipal,
    label: 'Interest before principal — the required minimum pays loan-level fees, then one APR bucket at a time',
  },
  {
    value: MinimumPaymentClassOrder.PrincipalBeforeInterest,
    label: 'Principal before interest — then interest, then fees that bear no interest',
  },
];

const accrualBaseOptions = [
  {
    value: AccrualBase.PrincipalOnly,
    label: 'Principal only (anti-compounding)',
  },
  {
    value: AccrualBase.FullBalance,
    label: 'Full balance (includes billed interest and fees)',
  },
];

const lateFeeStrategyOptions = [
  {
    value: LateFeeStrategy.PercentOfMinimum,
    label: 'Percent of minimum — min(5% × minimum payment, first late fee)',
  },
  {
    value: LateFeeStrategy.FlatTiered,
    label: 'Flat tiered — first/subsequent amount, capped at the missed minimum',
  },
];

const interestPostingPolicyOptions = [
  {
    value: InterestPostingPolicy.TrailingNextStatement,
    label: 'Trailing — interest appears on the next statement',
  },
  {
    value: InterestPostingPolicy.SameCycleStatementClose,
    label: 'Same cycle — interest appears at statement close',
  },
];

const graceStrategyOptions = [
  {
    value: GraceStrategy.Retroactive,
    label: 'Retroactive — deferred interest posts at the due date (dwelling-secured only)',
  },
  {
    value: GraceStrategy.Waive,
    label: 'Waive — unpaid portion only, current cycle only (Reg Z §1026.54)',
  },
];

const returnedPaymentFeeStrategyOptions = [
  {
    value: ReturnedPaymentFeeStrategy.Flat,
    label: 'Flat — the fee amount charged as-is (dwelling-secured only)',
  },
  {
    value: ReturnedPaymentFeeStrategy.Capped,
    label: 'Capped — min(fee, minimum due) (Reg Z §1026.52(b)(2)(i))',
  },
];

const pdfTemplateOptions = toPdfTemplateOptions(Object.values(PDF_TEMPLATES_BY_POLICY).flat());

const formConfig: SmartFormConfig<CreateProgramFormData> = {
  fields: [
    {
      name: 'program_id',
      label: 'Program ID',
      type: 'text',
      defaultValue: '',
      placeholder: 'Enter program ID',
    },
    {
      name: 'twispJournalId',
      label: 'Twisp Journal ID (optional)',
      type: 'text',
      defaultValue: '',
      placeholder: 'Leave blank to generate',
      helperText: 'Optional existing Twisp journal UUID. Blank lets Starlink generate one.',
    },
    {
      name: 'twispIsoConfigId',
      label: 'Twisp ISO Config ID (optional)',
      type: 'text',
      defaultValue: '',
      placeholder: 'Leave blank to generate',
      helperText: 'Optional existing Twisp ISO config UUID. Blank lets Starlink create one.',
    },
    {
      name: 'twispExposureAccountSetId',
      label: 'Twisp Exposure Account Set ID (optional)',
      type: 'text',
      defaultValue: '',
      placeholder: 'Leave blank to generate',
      helperText: 'Optional existing Twisp exposure account set UUID. Blank lets Starlink create one.',
    },
    {
      name: 'name',
      label: 'Program Name',
      type: 'text',
      defaultValue: '',
      placeholder: 'Enter program name',
    },
    {
      name: 'targetInterestRate',
      label: 'Target Interest Rate (%)',
      type: 'text',
      defaultValue: '',
      placeholder: 'e.g., 15.99',
    },
    {
      name: 'minApr',
      label: 'Min APR (%)',
      type: 'text',
      defaultValue: '',
      placeholder: 'e.g., 9.99',
    },
    {
      name: 'maxApr',
      label: 'Max APR (%)',
      type: 'text',
      defaultValue: '',
      placeholder: 'e.g., 24.99',
    },
    {
      name: 'interestPostingPolicy',
      label: 'Interest Posting Policy',
      type: 'select',
      required: true,
      options: interestPostingPolicyOptions,
      defaultValue: '',
      placeholder: 'Select interest posting policy',
      helperText:
        'Trailing pairs with the HomeLight template; same-cycle pairs with the LFCU or generic card template.',
    },
    {
      name: 'accrualBase',
      label: 'Interest Accrual Base',
      type: 'select',
      required: true,
      options: accrualBaseOptions,
      defaultValue: '',
      placeholder: 'Select accrual base',
      helperText: 'Choose whether interest accrues on principal only or the full balance.',
    },
    {
      name: 'pdfTemplate',
      label: 'Statement PDF Template',
      type: 'select',
      required: true,
      options: pdfTemplateOptions,
      defaultValue: '',
      placeholder: 'Select statement template',
      helperText: 'The generic card statement is for a card program with no branding of its own.',
    },
    {
      name: 'chargeDrawdownFee',
      label: 'Charge ACH Drawdown Fee',
      type: 'select',
      required: true,
      options: YES_NO_OPTIONS,
      defaultValue: '',
      placeholder: 'Select drawdown fee policy',
      helperText: 'When enabled, settled ACH drawdowns charge the program credit-line drawdown fee.',
    },
    {
      name: 'lateFeeStrategy',
      label: 'Late Fee Strategy',
      type: 'select',
      required: true,
      options: lateFeeStrategyOptions,
      defaultValue: '',
      placeholder: 'Select late fee strategy',
      helperText:
        'Percent uses 5% of the missed minimum, capped by the first amount. Flat uses first/subsequent amounts.',
    },
    {
      name: 'firstLateFeeAmount',
      label: 'First Late Fee Amount',
      required: true,
      component: CurrencyField,
      componentProps: {
        showCurrencySymbol: true,
      },
      defaultValue: '',
    },
    {
      name: 'subsequentLateFeeAmount',
      label: 'Subsequent Late Fee Amount',
      component: CurrencyField,
      componentProps: {
        showCurrencySymbol: true,
      },
      defaultValue: '',
      helperText: 'Required for flat tiered fees; ignored by percent-of-minimum fees.',
    },
    {
      name: 'returnedPaymentFeeCents',
      label: 'Returned Payment Fee',
      required: true,
      component: CurrencyField,
      componentProps: {
        showCurrencySymbol: true,
      },
      defaultValue: 0,
      helperText: 'Charged when an ACH repayment is returned by the bank.',
    },
    {
      name: 'returnedPaymentFeeStrategy',
      label: 'Returned Payment Fee Strategy',
      type: 'select',
      required: true,
      options: returnedPaymentFeeStrategyOptions,
      defaultValue: '',
      placeholder: 'Select returned payment fee strategy',
      helperText:
        'No default — choose deliberately. Credit-card programs must use Capped; it cannot be changed after creation.',
    },
    {
      name: 'foreignTransactionFeeRatePercent',
      label: 'Foreign Transaction Fee Rate (%)',
      type: 'text',
      required: true,
      defaultValue: '',
      placeholder: 'e.g., 1 for 1%',
      helperText: 'Percentage of the settled amount for transactions in a foreign currency. 0 turns the fee off.',
    },
    {
      name: 'cashAdvancesAllowed',
      label: 'Cash advances allowed',
      type: 'boolean',
      defaultValue: false,
      helperText:
        "Cash advances (ATM, cash merchants, cash back) are allowed at the ledger for the program's Lithic cards. Off declines them, and the card statement then prints no Cash Advances section.",
    },
    {
      name: 'cashAdvanceFeeRatePercent',
      label: 'Cash Advance Fee Rate (%)',
      type: 'text',
      required: true,
      defaultValue: '',
      placeholder: 'e.g., 3 for 3%',
      helperText:
        'The charge is the greater of this rate times the advance and the minimum below. 0 in both turns the fee off.',
    },
    {
      name: 'cashAdvanceFeeMinCents',
      label: 'Cash Advance Fee Minimum',
      required: true,
      component: CurrencyField,
      componentProps: {
        showCurrencySymbol: true,
        allowZero: true,
      },
      defaultValue: 0,
      helperText: 'Floor for the cash advance fee — charged when the rate would come out below this amount.',
    },
    {
      name: 'replacementCardFeeCents',
      label: 'Replacement Card Fee',
      required: true,
      component: CurrencyField,
      componentProps: {
        showCurrencySymbol: true,
        allowZero: true,
      },
      defaultValue: 0,
      helperText: "Charged for a card replaced at the cardholder's request. 0 turns the fee off.",
    },
    {
      name: 'paymentDueDateGracePeriodDays',
      label: 'Minimum days from statement close to payment due',
      type: 'text',
      defaultValue: '',
      placeholder: 'Number of days (0-28)',
    },
    {
      name: 'lateFeeGracePeriodDays',
      label: 'Late Fee Grace Period Days',
      type: 'text',
      defaultValue: '',
      placeholder: 'Number of days (0-28)',
    },
    {
      name: 'statementGenerateDay',
      label: 'Statement generation day',
      type: 'text',
      defaultValue: '',
      placeholder: 'Day of month (1-28)',
      helperText: 'Applies to newly boarded loans. Existing loans keep their generation and payment due days.',
    },
    {
      name: 'paymentDueDay',
      label: 'Payment due day',
      type: 'text',
      required: true,
      defaultValue: '',
      placeholder: 'Day of month (1-28)',
    },
    {
      name: 'autopayMinimum',
      label: 'Autopay: minimum payment',
      type: 'boolean',
      defaultValue: false,
      helperText: 'The autopay options members of this program may enrol in. Offer at least one.',
    },
    {
      name: 'autopayStatement',
      label: 'Autopay: statement balance',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'autopayFull',
      label: 'Autopay: full balance',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'minimumPaymentStrategy',
      label: 'Minimum Payment Strategy',
      type: 'select',
      required: true,
      options: minimumPaymentStrategyOptions,
      defaultValue: MinimumPaymentStrategy.HelocCumulative,
    },
    {
      name: 'minimumPaymentAllocationOrder',
      label: 'Minimum Payment Allocation Order',
      type: 'select',
      required: true,
      options: minimumPaymentAllocationOrderOptions,
      defaultValue: '',
      placeholder: 'Select minimum payment allocation order',
      helperText:
        'No default — choose deliberately. Only the required-minimum part of a payment follows it; the part above the minimum always pays the highest APR first. It cannot be changed after creation.',
    },
    {
      name: 'minimumPaymentClassOrder',
      label: 'Minimum Payment Class Order',
      type: 'select',
      required: true,
      options: minimumPaymentClassOrderOptions,
      defaultValue: '',
      placeholder: 'Select minimum payment class order',
      helperText:
        'No default — choose deliberately; only the required-minimum part of a payment follows it, and it cannot be changed after creation.',
    },
    {
      name: 'graceStrategy',
      label: 'Grace Strategy',
      type: 'select',
      required: true,
      options: graceStrategyOptions,
      defaultValue: '',
      placeholder: 'Select grace strategy',
      helperText:
        'No default — choose deliberately. Credit-card programs must use Waive; it cannot be changed after creation.',
    },
    {
      name: 'minimumPaymentRatePercent',
      label: 'Minimum Payment Rate (%)',
      type: 'text',
      required: true,
      defaultValue: '',
      placeholder: 'e.g., 2 for 2%',
      helperText: 'Percentage of outstanding balance (0–100).',
      conditional: creditCardStrategyCondition,
    },
    {
      name: 'minimumPaymentFloorCents',
      label: 'Minimum Payment Floor',
      required: true,
      component: CurrencyField,
      componentProps: {
        showCurrencySymbol: true,
      },
      defaultValue: 0,
      helperText: 'Floor applied when percent-of-balance is below this amount.',
      conditional: creditCardStrategyCondition,
    },
  ],
  schema: createProgramSchema,
  defaultValues: {
    program_id: '',
    twispJournalId: '',
    twispIsoConfigId: '',
    twispExposureAccountSetId: '',
    name: '',
    targetInterestRate: '',
    minApr: '',
    maxApr: '',
    firstLateFeeAmount: 0,
    returnedPaymentFeeCents: 0,
    foreignTransactionFeeRatePercent: '',
    cashAdvancesAllowed: false,
    autopayMinimum: false,
    autopayStatement: false,
    autopayFull: false,
    cashAdvanceFeeRatePercent: '',
    cashAdvanceFeeMinCents: 0,
    replacementCardFeeCents: 0,
    paymentDueDateGracePeriodDays: '',
    lateFeeGracePeriodDays: '',
    statementGenerateDay: '',
    paymentDueDay: '',
    minimumPaymentStrategy: MinimumPaymentStrategy.HelocCumulative,
    minimumPaymentRatePercent: '',
    minimumPaymentFloorCents: 0,
  },
};

export const CreateProgramComponent = ({ api }: ModalProps<undefined, true>) => {
  const { handleMutation } = useMutationWithToast();

  const { data: partnersData } = useListPartnersQuery();
  const [createProgram, { loading }] = useCreateProgramMutation(getProgramMutationRefreshOptions());

  const partnerOptions = (partnersData?.listPartners ?? []).flatMap((partner) =>
    partner ? [{ value: partner.id, label: partner.active ? partner.name : `${partner.name} (inactive)` }] : [],
  );

  const config: SmartFormConfig<CreateProgramFormData> = {
    ...formConfig,
    fields: [
      {
        name: 'partnerId',
        label: 'Partner',
        type: 'select',
        required: true,
        options: partnerOptions,
        defaultValue: '',
        placeholder: 'Select partner',
        helperText: 'The partner this program belongs to.',
      },
      ...(formConfig.fields ?? []),
    ],
  };

  const handleSubmit = (data: CreateProgramFormData) => {
    const isCreditCard = data.minimumPaymentStrategy === MinimumPaymentStrategy.CreditCardPercentOfBalance;

    const variables: CreateProgramInput = {
      id: data.program_id,
      partnerId: data.partnerId,
      name: data.name,
      targetInterestRate: parseFloat(data.targetInterestRate),
      minApr: parseFloat(data.minApr),
      maxApr: parseFloat(data.maxApr),
      accrualBase: data.accrualBase,
      interestPostingPolicy: data.interestPostingPolicy,
      lateFeeStrategy: data.lateFeeStrategy,
      chargeDrawdownFee: data.chargeDrawdownFee === 'true',
      pdfTemplate: data.pdfTemplate,
      firstLateFeeAmount: data.firstLateFeeAmount,
      returnedPaymentFeeCents: data.returnedPaymentFeeCents,
      returnedPaymentFeeStrategy: data.returnedPaymentFeeStrategy,
      foreignTransactionFeeRate: percentStringToRate(data.foreignTransactionFeeRatePercent),
      cashAdvancesAllowed: data.cashAdvancesAllowed,
      cashAdvanceFeeRate: percentStringToRate(data.cashAdvanceFeeRatePercent),
      cashAdvanceFeeMinCents: data.cashAdvanceFeeMinCents,
      replacementCardFeeCents: data.replacementCardFeeCents,
      autopayPaymentTypes: [
        ...(data.autopayMinimum ? ['minimum'] : []),
        ...(data.autopayStatement ? ['statement'] : []),
        ...(data.autopayFull ? ['full'] : []),
      ],
      paymentDueDateGracePeriodDays: parseInt(data.paymentDueDateGracePeriodDays),
      lateFeeGracePeriodDays: parseInt(data.lateFeeGracePeriodDays),
      statementGenerateDay: Number(data.statementGenerateDay),
      paymentDueDay: Number(data.paymentDueDay),
      minimumPaymentStrategy: data.minimumPaymentStrategy,
      minimumPaymentAllocationOrder: data.minimumPaymentAllocationOrder,
      minimumPaymentClassOrder: data.minimumPaymentClassOrder,
      minimumPaymentRate: isCreditCard ? percentStringToRate(data.minimumPaymentRatePercent ?? '0') : null,
      minimumPaymentFloorCents: isCreditCard ? (data.minimumPaymentFloorCents ?? 0) : null,
      graceStrategy: data.graceStrategy,
    };

    if (data.twispJournalId) variables.twispJournalId = data.twispJournalId;

    if (data.twispIsoConfigId) variables.twispIsoConfigId = data.twispIsoConfigId;

    if (data.twispExposureAccountSetId) variables.twispExposureAccountSetId = data.twispExposureAccountSetId;

    if (data.subsequentLateFeeAmount !== undefined) variables.subsequentLateFeeAmount = data.subsequentLateFeeAmount;

    void handleMutation({
      mutation: () => createProgram({ variables: { input: variables } }),
      extractSuccess: (result) => !!result?.data?.createProgram,
      debugLabel: 'create-program',
      toast: {
        success: 'Program created successfully',
        error: 'Failed to create program',
        loading: 'Creating program...',
      },
      onSuccess: () => {
        api.close(true);
      },
    });
  };

  return createFormComponent({
    config,
    handleSubmit,
    loading,
  });
};
